// ── PDF extraction pipeline configuration ────────────────────────────────────
// All feature flags below are environment-driven so the extraction strategy
// can be tuned per environment (or per client) without a code change.
//
// ── Regex vs OCR — which is better for the data we extract? ─────────────────
// The fields we pull (accountNumber, mrn, payerName, address, city, state,
// zip, firstName, lastName, facilityName) are mostly short, structured
// tokens with a well-defined shape (alphanumeric IDs, 2-letter states,
// 5/9-digit zips). For those:
//   - Regex over the PDF's embedded text layer (pdf.js) is near-instant
//     (no rendering/rasterization) and exact — it reads the same characters
//     the document actually contains, so there is no risk of character
//     confusion (e.g. "0"/"O", "1"/"I"/"l") that image-based OCR introduces.
//     This makes regex the right *default* and primary strategy for
//     born-digital PDFs (the vast majority of inbound documents).
//   - OCR (tesseract.js) is only useful when a page has no usable text layer
//     at all (a scanned/faxed page rendered purely as an image). It is
//     5-20x slower per page (canvas render + WASM recognition) and less
//     reliable for exact identifiers, so it should stay a fallback rather
//     than a primary strategy. It is now gated behind its own flag so
//     environments that never receive scanned documents can disable it
//     entirely for a large speed win.
//   - Azure AI (existing REACT_APP_RRD_USE_AZURE_AI flag) remains the best
//     option for unstructured/narrative fields (names split across lines,
//     multi-line addresses) and is used only when local extraction
//     (regex/OCR) is incomplete.
// Recommended defaults: Regex ON, OCR ON (safe fallback), Azure AI ON.
// Turn OCR off first if extraction speed needs to improve and source
// documents are known to always have a real text layer.

const parsePositiveInteger = (value: string | undefined, fallback: number): number => {
  const parsedValue = Number.parseInt((value || '').trim(), 10);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

const parseBooleanFlag = (value: string | undefined, fallback: boolean): boolean => {
  const normalized = (value ?? '').trim().toLowerCase();
  if (!normalized) return fallback;
  return normalized === 'true';
};

/** How many pages (from the front of the document) field extraction reads. */
export const PDF_EXTRACTION_PAGE_COUNT = parsePositiveInteger(
  process.env.REACT_APP_RRD_PDF_EXTRACT_PAGE_COUNT,
  1
);

/** Maximum number of pages (from the front of the document) used for classification. */
export const PDF_CLASSIFICATION_PAGE_COUNT = 5;

/** Whether the Azure OpenAI classification/extraction call is allowed at all. */
export const AZURE_AI_CLASSIFICATION_ENABLED = parseBooleanFlag(
  process.env.REACT_APP_RRD_USE_AZURE_AI,
  true
);

/** Whether OCR (tesseract.js) may run as a fallback for pages with no text layer. */
export const OCR_EXTRACTION_ENABLED = parseBooleanFlag(
  process.env.REACT_APP_RRD_USE_OCR_EXTRACTION,
  true
);

/** Whether local regex-based field parsing runs at all. */
export const REGEX_EXTRACTION_ENABLED = parseBooleanFlag(
  process.env.REACT_APP_RRD_USE_REGEX_EXTRACTION,
  true
);
